import axios from 'axios';

const API_BASE_URL = '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export interface Device {
  id: number;
  name: string;
  uniqueid: string;
  status: string | null;
  lastupdate: string | null;
  model: string | null;
  category: string | null;
  attributes: string | null;
}

export interface Position {
  id: number;
  deviceid: number;
  fixtime: string;
  latitude: number;
  longitude: number;
  /** @deprecated knots — use speed_kmh */
  speed: number;
  speed_kmh: number;   // km/h, computed by backend (speed * 1.852)
  course: number;
  altitude: number | null;
  accuracy: number | null;
  attributes: string | null;
}

export const login = async (username: string, password: string) => {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);
  const response = await api.post('/api/auth/login', formData, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/api/auth/me');
  return response.data;
};

// Samoobsługowa zmiana hasła (2026-09-20) — dowolny zalogowany user, zmienia
// WYŁĄCZNIE własne hasło (backend wyprowadza traccar_user_id z JWT, nie z
// żadnego parametru tutaj). Zob. backend/app/api/endpoints/auth.py.
export const changePassword = async (data: {
  current_password: string;
  new_password: string;
}): Promise<{ success: boolean }> => {
  const response = await api.post('/api/auth/change-password', data);
  return response.data;
};

export const getDevices = async (): Promise<Device[]> => {
  const response = await api.get('/api/devices/');
  return response.data;
};

export const getDevicePosition = async (deviceId: number): Promise<Position> => {
  const response = await api.get(`/api/devices/${deviceId}/position`);
  return response.data;
};

export const getDeviceHistory = async (deviceId: number, hours: number = 24): Promise<Position[]> => {
  const response = await api.get(`/api/devices/${deviceId}/history?hours=${hours}`);
  return response.data;
};

export const getDrivingTime = async (deviceId: number): Promise<{ driving_seconds: number; driving_time: string; drive_start: string | null; drive_end: string | null }> => {
  const response = await api.get(`/api/devices/${deviceId}/driving-time`);
  return response.data;
};

export interface DrivingReportRow {
  device_id: number;
  device_name: string;
  date: string;          // YYYY-MM-DD
  drive_start: string | null;
  drive_end: string | null;
  driving_time: string;  // HH:MM
  driving_seconds: number;
  distance_km: number;
}

export interface SpeedReportRow {
  device_id: number;
  device_name: string;
  category: string;
  datetime: string;    // YYYY-MM-DD HH:MM (Warsaw)
  speed_kmh: number;
  limit_kmh: number;
  excess_kmh: number;
  latitude: number;
  longitude: number;
}

export const getSpeedReport = async (
  deviceId: number | 'all',
  dateFrom: string,
  dateTo: string,
  threshold?: number,
): Promise<SpeedReportRow[]> => {
  const params: Record<string, string> = {
    device_id: String(deviceId),
    date_from: dateFrom,
    date_to: dateTo,
  };
  if (threshold !== undefined) params.threshold = String(threshold);
  const response = await api.get('/api/reports/speed', { params });
  return response.data;
};

export const getDrivingReport = async (
  deviceId: number | 'all',
  dateFrom: string,
  dateTo: string,
): Promise<DrivingReportRow[]> => {
  const response = await api.get('/api/reports/driving', {
    params: { device_id: String(deviceId), date_from: dateFrom, date_to: dateTo },
  });
  return response.data;
};

export interface StopsReportRow {
  device_id: number;
  device_name: string;
  date: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  latitude: number;
  longitude: number;
}

export interface StopsDetailRow {
  event_type: 'stop' | 'parking';
  start_time: string;
  end_time: string;
  duration_minutes: number;
  latitude: number;
  longitude: number;
  address: string;
}

export const getStopsDetailReport = async (
  deviceId: number,
  dateFrom: string,
  dateTo: string,
): Promise<StopsDetailRow[]> => {
  const response = await api.get(`/api/devices/${deviceId}/stops-report`, {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return response.data;
};

export interface VehicleConfigOut {
  device_id: number;
  device_name: string;
  uniqueid: string;
  phone: string | null;
  registration_number: string | null;
  tank_capacity_liters: number | null;
  fuel_alert_threshold_liters: number | null;
  fuel_source: string;
  speed_limit_kmh: number | null;
  notes: string | null;
  has_tachograph: boolean;
  armed: boolean;
  armed_at: string | null;
  armed_threshold_m: number;
  odometer_gps_offset: number;
  device_type: string | null;
  can_display_fields: Record<string, boolean>;
  din2_label: string | null;
  din2_icon: string | null;
  din3_label: string | null;
  din3_icon: string | null;
  dout1_label: string | null;
  dout1_icon: string | null;
  dout2_label: string | null;
  dout2_icon: string | null;
  etoll_enabled: boolean;
  tacho_ddd_enabled: boolean;
  soms_enabled: boolean;
  wire1_labels: any[];
  din_config: any[];
  is_hybrid?: boolean;
}

export interface CanFieldInfo {
  key: string;
  label: string;
  last_value: number | null;
  enabled: boolean;
}

export interface OdometerToday {
  can_start_km: number | null;
  can_current_km: number | null;
  can_today_km: number | null;
  gps_today_km: number | null;
  fuel_today_l: number | null;
}

export const getVehicleConfigs = async (): Promise<VehicleConfigOut[]> => {
  const response = await api.get('/api/vehicles/config');
  return response.data;
};

export const deleteDeviceHistory = async (deviceId: number): Promise<void> => {
  await api.delete(`/api/devices/${deviceId}/history`);
};

export const updateVehicleConfig = async (
  deviceId: number,
  config: Omit<VehicleConfigOut, 'device_id' | 'device_name' | 'uniqueid' | 'armed' | 'armed_at'> & { name?: string; category?: string | null },
): Promise<VehicleConfigOut> => {
  const response = await api.put(`/api/vehicles/${deviceId}/config`, config);
  return response.data;
};

export const getCanFields = async (deviceId: number): Promise<CanFieldInfo[]> => {
  const response = await api.get(`/api/vehicles/${deviceId}/can-fields`);
  return response.data;
};

export const getOdometerToday = async (deviceId: number): Promise<OdometerToday> => {
  const response = await api.get(`/api/vehicles/${deviceId}/odometer-today`);
  return response.data;
};

export interface FuelReportRow {
  device_id: number;
  device_name: string;
  date: string;
  fuel_used_liters: number;
  distance_km: number;
  avg_consumption: number | null;
  fuel_source_used: string;
}

export const getFuelReport = async (
  deviceId: number | 'all',
  dateFrom: string,
  dateTo: string,
): Promise<FuelReportRow[]> => {
  const response = await api.get('/api/reports/fuel', {
    params: { device_id: String(deviceId), date_from: dateFrom, date_to: dateTo },
  });
  return response.data;
};

export const getStopsReport = async (
  deviceId: number | 'all',
  dateFrom: string,
  dateTo: string,
  minDuration?: number,
): Promise<StopsReportRow[]> => {
  const params: Record<string, string> = {
    device_id: String(deviceId),
    date_from: dateFrom,
    date_to: dateTo,
  };
  if (minDuration !== undefined) params.min_duration = String(minDuration);
  const response = await api.get('/api/reports/stops', { params });
  return response.data;
};

export interface DriverTimeRow {
  device_id: number;
  device_name: string;
  date: string;
  drive_time_minutes: number;
  break_time_minutes: number;
  total_work_minutes: number;
  day_remaining_minutes: number;
  week_number: number;
}

export interface DriverTimeWeekDay {
  date: string;
  drive_time_minutes: number;
  break_time_minutes: number;
  total_work_minutes: number;
  day_remaining_minutes: number;
}

export interface DriverTimeWeeklyRow {
  device_id: number;
  device_name: string;
  week_number: number;
  year: number;
  total_drive_minutes: number;
  total_work_minutes: number;
  week_remaining_minutes: number;
  days: DriverTimeWeekDay[];
}

export const getDriverTimeReport = async (
  deviceId: number | 'all',
  dateFrom: string,
  dateTo: string,
): Promise<DriverTimeRow[]> => {
  const response = await api.get('/api/reports/driver-time', {
    params: { device_id: String(deviceId), date_from: dateFrom, date_to: dateTo },
  });
  return response.data;
};

export const getDeviceHistoryDate = async (
  deviceId: number,
  date: string,
): Promise<Position[]> => {
  const response = await api.get(`/api/devices/${deviceId}/history-date`, { params: { date } });
  return response.data;
};

export const getDriverTimeWeeklyReport = async (
  deviceId: number | 'all',
  year: number,
  weekNumber: number,
): Promise<DriverTimeWeeklyRow[]> => {
  const response = await api.get('/api/reports/driver-time/weekly', {
    params: { device_id: String(deviceId), year, week_number: weekNumber },
  });
  return response.data;
};

export interface CircleGeometry {
  center: [number, number];  // [lat, lng]
  radius: number;             // meters
}

export interface PolygonGeometry {
  points: [number, number][]; // [[lat, lng], ...]
}

export interface Geofence {
  id: number;
  name: string;
  geometry_type: 'circle' | 'polygon';
  geometry: CircleGeometry | PolygonGeometry;
  color: string;
  notify_enter: boolean;
  notify_exit: boolean;
  active: boolean;
  device_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface GeofenceInput {
  name: string;
  geometry_type: 'circle' | 'polygon';
  geometry: CircleGeometry | PolygonGeometry;
  color?: string;
  notify_enter?: boolean;
  notify_exit?: boolean;
  active?: boolean;
  device_ids?: number[];
}

export const getGeofences = async (): Promise<Geofence[]> => {
  const response = await api.get('/api/geofences/');
  return response.data;
};

export const createGeofence = async (data: GeofenceInput): Promise<Geofence> => {
  const response = await api.post('/api/geofences/', data);
  return response.data;
};

export const updateGeofence = async (id: number, data: GeofenceInput): Promise<Geofence> => {
  const response = await api.put(`/api/geofences/${id}`, data);
  return response.data;
};

export const deleteGeofence = async (id: number): Promise<void> => {
  await api.delete(`/api/geofences/${id}`);
};

export type AlertType = 'zone_enter' | 'zone_exit' | 'armed_movement';

export interface Alert {
  id: number;
  device_id: number;
  device_name: string;
  type: AlertType | string;
  geofence_id: number | null;
  geofence_name: string | null;
  fixtime: string;
  latitude: number;
  longitude: number;
  message: string | null;
  acknowledged: boolean;
  ack_at: string | null;
  created_at: string;
}

export const armDevice = async (deviceId: number): Promise<VehicleConfigOut> => {
  const response = await api.post(`/api/vehicles/${deviceId}/arm`);
  return response.data;
};

export const disarmDevice = async (deviceId: number): Promise<VehicleConfigOut> => {
  const response = await api.post(`/api/vehicles/${deviceId}/disarm`);
  return response.data;
};

export const getAlerts = async (
  opts: { unacknowledged?: boolean; limit?: number } = {},
): Promise<Alert[]> => {
  const params: Record<string, string> = {};
  if (opts.unacknowledged !== undefined) params.unacknowledged = String(opts.unacknowledged);
  if (opts.limit !== undefined) params.limit = String(opts.limit);
  const response = await api.get('/api/alerts/', { params });
  return response.data;
};

export const ackAlert = async (id: number): Promise<Alert> => {
  const response = await api.post(`/api/alerts/${id}/ack`);
  return response.data;
};

export const ackAllAlerts = async (): Promise<void> => {
  await api.post('/api/alerts/ack-all');
};

export interface DeviceAddInput {
  name:     string;
  uniqueid: string;
  model?:   string;
  category?: string;
}

export interface DeviceUpdateInput {
  name:      string;
  model?:    string;
  category?: string;
}

export const addDevice = async (data: DeviceAddInput): Promise<Device> => {
  const response = await api.post('/api/devices/add', data);
  return response.data;
};

export const updateDeviceMeta = async (deviceId: number, data: DeviceUpdateInput): Promise<Device> => {
  const response = await api.put(`/api/devices/${deviceId}/update`, data);
  return response.data;
};

export const removeDevice = async (deviceId: number): Promise<void> => {
  await api.delete(`/api/devices/${deviceId}/remove`);
};

export interface PendingDevice {
  id: number;
  name: string;
  uniqueid: string;
  protocol: string | null;
  approval_status: string;
  first_seen_at: string | null;
}

export const getPendingDevices = async (): Promise<PendingDevice[]> => {
  const response = await api.get('/api/devices/pending');
  return response.data;
};

export const approveDevice = async (deviceId: number): Promise<void> => {
  await api.post(`/api/devices/${deviceId}/approve`);
};

export const rejectDevice = async (deviceId: number): Promise<void> => {
  await api.post(`/api/devices/${deviceId}/reject`);
};

export const getFuelReportDetailed = async (
  deviceId: number,
  dateFrom: string,
  dateTo: string,
) => {
  const response = await api.get(`/api/devices/${deviceId}/fuel-report`, {
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return response.data;
};

// ── Panel admina: konta klientów (/api/admin/clients) ───────────────────────

export interface AdminClientDeviceSummary {
  id: number;
  name: string | null;
  // Tylko w odpowiedzi GET /api/admin/clients (list_clients, 2026-09-19) —
  // true gdy urządzenie pochodzi ze współdzielenia (fleet_device_sharing),
  // nie z własnego przypisania klienta. Nieobecne w odpowiedziach
  // create_client/add_client_devices (te nadal budują z Traccar API, bez
  // konceptu współdzielenia).
  shared?: boolean;
}

export interface AdminClient {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  administrator: boolean;
  disabled: boolean;
  deviceLimit: number;
  devices: AdminClientDeviceSummary[];
  // Krok B (2026-09-20): fleet_clients.id, null gdy user spoza M2 (konto
  // sprzed backfillu). Odkąd POST /api/admin/tenants/{client_id}/users
  // pozwala dodać więcej niż jednego Traccar usera pod ten sam client_id,
  // kilka wierszy tej listy może dzielić ten sam client_id — zob.
  // other_users_same_client poniżej.
  client_id: number | null;
  // Emaile innych Traccar userów tego samego client_id (bez samego siebie) —
  // wskaźnik do wizualnego oznaczenia grupowania, bez pełnego
  // przeprojektowania tabeli na grupowaną. Puste gdy client_id is null lub
  // user jest jedynym userem swojego klienta.
  other_users_same_client: string[];
}

export interface AdminClientCreateInput {
  name: string;
  email: string;
  device_ids: number[];
}

export interface AdminClientCreateResult {
  id: number;
  name: string | null;
  email: string;
  temporary_password: string;
  devices: AdminClientDeviceSummary[];
}

export interface AdminClientUserCreateResult {
  id: number;
  name: string | null;
  email: string;
  temporary_password: string;
  // BRAK pola devices — POST /api/admin/tenants/{client_id}/users celowo
  // nie mirroruje żadnych urządzeń (zob. CHANGELOG.md, Krok B), więc nie ma
  // czego tu zwrócić, w przeciwieństwie do AdminClientCreateResult.
}

export interface AdminClientAddDevicesResult {
  id: number;
  email: string;
  added: number[];
  already_assigned: number[];
  devices: AdminClientDeviceSummary[];
}

export interface AdminClientPasswordResetResult {
  id: number;
  email: string;
  temporary_password: string;
}

export interface AdminClientToggleResult {
  id: number;
  email: string;
  disabled: boolean;
}

export const getAdminClients = async (): Promise<AdminClient[]> => {
  const response = await api.get('/api/admin/clients');
  return response.data;
};

export const createAdminClient = async (
  data: AdminClientCreateInput,
): Promise<AdminClientCreateResult> => {
  const response = await api.post('/api/admin/clients', data);
  return response.data;
};

// Krok B (2026-09-20): dodaje KOLEJNEGO usera do JUŻ ISTNIEJĄCEGO client_id
// (fleet_clients.id) — INNE id niż userId w addAdminClientDevices/etc.
// powyżej (tamte operują na Traccar user id). Zob. też komentarz przy
// DeviceSharingClientOption niżej — ten sam problem nazewniczy z dwoma id.
export const addClientUser = async (
  clientId: number,
  data: { name: string; email: string },
): Promise<AdminClientUserCreateResult> => {
  const response = await api.post(`/api/admin/tenants/${clientId}/users`, data);
  return response.data;
};

export interface ImpersonateResult {
  access_token: string;
  token_type: string;
  impersonated_email: string;
}

// Impersonacja admina (2026-09-20) — token zwrócony tutaj jest CZYSTO
// PODGLĄDOWY: backend blokuje globalnie każdy zapis (POST/PUT/PATCH/DELETE)
// wykonany tym tokenem (block_writes_during_impersonation, app/core/
// security.py), niezależnie od tego, co robi frontend. userId to Traccar
// user id (AdminClient.id), NIE client_id — ta sama przestrzeń co
// addAdminClientDevices/resetAdminClientPassword poniżej.
export const impersonateClient = async (userId: number): Promise<ImpersonateResult> => {
  const response = await api.post(`/api/admin/clients/${userId}/impersonate`);
  return response.data;
};

export const addAdminClientDevices = async (
  userId: number,
  deviceIds: number[],
): Promise<AdminClientAddDevicesResult> => {
  const response = await api.post(`/api/admin/clients/${userId}/devices`, { device_ids: deviceIds });
  return response.data;
};

export const resetAdminClientPassword = async (
  userId: number,
): Promise<AdminClientPasswordResetResult> => {
  const response = await api.post(`/api/admin/clients/${userId}/reset-password`);
  return response.data;
};

export const disableAdminClient = async (userId: number): Promise<AdminClientToggleResult> => {
  const response = await api.post(`/api/admin/clients/${userId}/disable`);
  return response.data;
};

export const enableAdminClient = async (userId: number): Promise<AdminClientToggleResult> => {
  const response = await api.post(`/api/admin/clients/${userId}/enable`);
  return response.data;
};

// ── Panel admina: współdzielenie urządzeń (/api/admin/device-sharing) ──────
// UWAGA: to jest fleet_clients.id (M2 client_id) — INNE id niż AdminClient.id
// powyżej (to konto Traccara). Stąd osobne endpointy /clients i /devices
// tutaj, zamiast reużycia getAdminClients()/getDevices().

export interface DeviceSharingClientOption {
  id: number;
  name: string;
}

export interface DeviceSharingDeviceOption {
  id: number;
  name: string;
  owner_client_id: number | null;
  owner_client_name: string | null;
}

export interface DeviceSharing {
  id: number;
  device_id: number;
  device_name: string;
  owner_client_id: number;
  owner_client_name: string;
  shared_client_id: number;
  shared_client_name: string;
  granted_by_admin: number | null;
  granted_by_email: string | null;
  active: boolean;
  created_at: string | null;
  notes: string | null;
}

export interface DeviceSharingCreateInput {
  device_id: number;
  shared_client_id: number;
  notes?: string;
}

export const getDeviceSharingClients = async (): Promise<DeviceSharingClientOption[]> => {
  const response = await api.get('/api/admin/device-sharing/clients');
  return response.data;
};

export const getDeviceSharingDevices = async (): Promise<DeviceSharingDeviceOption[]> => {
  const response = await api.get('/api/admin/device-sharing/devices');
  return response.data;
};

export const getDeviceSharings = async (): Promise<DeviceSharing[]> => {
  const response = await api.get('/api/admin/device-sharing/');
  return response.data;
};

export const createDeviceSharing = async (
  data: DeviceSharingCreateInput,
): Promise<DeviceSharing> => {
  const response = await api.post('/api/admin/device-sharing/', data);
  return response.data;
};

export const revokeDeviceSharing = async (sharingId: number): Promise<void> => {
  await api.delete(`/api/admin/device-sharing/${sharingId}`);
};

export default api;
